"use client"
import { authClient } from "@/lib/auth-client"
import { ForgotPasswordSchema } from '@/server/schemas/auth.schema'
import { faArrowLeft, faCheckCircle, faEnvelope, faKey } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Button, Card, CardBody, CardHeader, Divider, Input, Link } from "@heroui/react"
import { zodResolver } from "@hookform/resolvers/zod"
import { useMutation } from '@tanstack/react-query'
import { useTranslations } from "next-intl"
import { useState } from "react"
import { Controller, useForm } from "react-hook-form"
import toast from 'react-hot-toast'
import { z } from "zod"

type FormType = z.infer<typeof ForgotPasswordSchema>
const defaultValues: FormType = {
  email: ""
}

export default function ForgotPasswordPage() {
  const t = useTranslations("ForgotPassword")
  const [success, setSuccess] = useState(false)

  const {
    handleSubmit,
    formState: { errors },
    control
  } = useForm<FormType>({
    resolver: zodResolver(ForgotPasswordSchema),
    defaultValues
  })

  const mutation = useMutation({
    mutationFn: async (data: FormType) => {
      const { error } = await authClient.requestPasswordReset({
        email: data.email,
        redirectTo: "/auth/reset-password"
      })
      if (error) {
        throw error
      }
    },
    onSuccess: () => {
      setSuccess(true)
    },
    onError: () => {
      toast.error(t("error"))
    }
  })

  const onSubmit = (data: FormType) => {
    mutation.mutate(data)
  }

  const form = (
    <form className="flex flex-col gap-4" onSubmit={handleSubmit(onSubmit)}>
      <div className="bg-default-50 dark:bg-default-900/20 rounded-lg p-4 mb-2">
        <p className="text-small text-default-600">{t("message")}</p>
      </div>

      <Controller
        name="email"
        control={control}
        render={({ field }) => {
          return (
            <Input
              label={t("email")}
              variant="bordered"
              placeholder={t("enterEmailAddress")}
              {...field}
              isInvalid={!!errors.email}
              color={!!errors.email ? "danger" : "default"}
              errorMessage={errors.email?.message}
              classNames={{
                input: "text-sm",
                label: "text-sm font-medium"
              }}
            />
          )
        }}
      />

      <Button
        type="submit"
        color="primary"
        size="lg"
        className="w-full font-semibold"
        isLoading={mutation.isPending}
        startContent={!mutation.isPending && <FontAwesomeIcon icon={faEnvelope} />}
      >
        {t("sendLink")}
      </Button>

      <div className="text-center">
        <Link
          size="sm"
          className="text-primary hover:text-primary-600 font-medium inline-flex items-center gap-2"
          href="/auth/sign-in"
        >
          <FontAwesomeIcon icon={faArrowLeft} className="text-xs" />
          {t("signIn")}
        </Link>
      </div>
    </form>
  )

  let body
  if (success) {
    body = (
      <div className="text-center space-y-4">
        <div className="flex items-center justify-center w-16 h-16 bg-success/10 rounded-full mx-auto">
          <FontAwesomeIcon icon={faCheckCircle} className="text-2xl text-success" />
        </div>
        <div className="space-y-2">
          <h3 className="text-lg font-semibold text-success">{t("emailSent")}</h3>
          <p className="text-small text-default-500">{t("success")}</p>
        </div>
        <Button
          as={Link}
          href="/auth/sign-in"
          variant="bordered"
          color="primary"
          startContent={<FontAwesomeIcon icon={faArrowLeft} />}
        >
          {t("backToSignIn")}
        </Button>
      </div>
    )
  } else {
    body = form
  }

  return (
    <div className="flex h-full w-full items-center justify-center p-4">
      <div className="w-full max-w-md">
        <Card className="border-none shadow-2xl bg-content1/95 backdrop-blur-md">
          {success ? (
            <CardBody className="px-8 py-8">
              {body}
            </CardBody>
          ) : (
            <>
              <CardHeader className="flex flex-col gap-1 items-center pb-6 pt-8">
                <div className="flex items-center justify-center w-16 h-16 bg-warning/10 rounded-full mb-4">
                  <FontAwesomeIcon icon={faKey} className="text-2xl text-warning" />
                </div>
                <h1 className="text-2xl font-bold text-foreground">{t("title")}</h1>
                <p className="text-small text-default-500">{t("resetPassword")}</p>
              </CardHeader>

              <Divider />

              <CardBody className="px-8 py-6">
                {body}
              </CardBody>
            </>
          )}
        </Card>
      </div>
    </div>
  )
}
